/**
 * Planned routes of public air traffic (R160; docs/specs/public-routes.md; decisions 924, 933, 934). Clicking an Oz
 * Runways or AvPlan aircraft asks that feed for the pilot's shared plan, once, for that aircraft only; an airline
 * flight gets the straight line (the great circle) between the two airports of its published schedule (adsbdb,
 * decision 385), labelled as such. Nothing is polled and nothing is kept: the AvPlan device id that the plan's address
 * needs is read from `live.json` at the click and dropped after the one request, and the plan's own callsign is never
 * read (decision 933). A route is not shown when the aircraft is over 100 nm from it or an AvPlan plan's last time is
 * over 12 hours old.
 *
 * The same rules serve Teslyn Field (its Rust core asks the fixed addresses, `src-tauri/src/traffic.rs`) and Teslyn
 * Cloud (the `traffic-route` Edge Function, built from this module by `tools/dev/copy-edge-shared.mjs`), so every
 * exported type is declared here.
 */
import { AU_AERODROMES, auAerodromeList } from './aerodromes.js';
import { airlineCallsign } from './aircraft-register.js';

// ---------------------------------------------------------------------------------------------- types

export type PublicRouteSource = 'ozrunways' | 'avplan' | 'airline';

/** One point of a route. Times are epoch ms UTC. */
export interface PublicRoutePoint {
  readonly lat: number;
  readonly lon: number;
  /** The waypoint's name in the plan (YBWW, IDROP); null when the feed gives coordinates only. */
  readonly ident: string | null;
  readonly altFt: number | null;
  /** The ETA the plan gives (AvPlan's `eta`, else its `planedEta`). */
  readonly etaMs: number | null;
  /** When the aircraft passed it (AvPlan's `ata`). */
  readonly passedMs: number | null;
  /** A landing point of the plan. */
  readonly landing: boolean;
}

/** A place a route starts or ends: its code and, when the aerodrome list knows it, its name. */
export interface PublicRoutePlace {
  readonly code: string;
  readonly name: string | null;
}

export interface PublicRoute {
  readonly source: PublicRouteSource;
  readonly points: readonly PublicRoutePoint[];
  /** The airline's published schedule (dashed, labelled), not a pilot's plan. */
  readonly schedule: boolean;
  readonly origin: PublicRoutePlace | null;
  readonly destination: PublicRoutePlace | null;
}

/** What a click asks for, from the aircraft's id on the map (`<source>:<account>:<device>`). */
export type PublicRouteAsk =
  | { readonly source: 'ozrunways'; readonly key: string }
  /** `callsign` is '' for an AvPlan aircraft without one; `deviceId` is then its own entry's. */
  | { readonly source: 'avplan'; readonly callsign: string; readonly deviceId: string }
  | { readonly source: 'airline'; readonly callsign: string };

/** One feed answer. */
export interface PublicRouteHttp {
  readonly status: number;
  readonly body: string;
}

/** The three requests a route can need; Teslyn Field and Teslyn Cloud each make them their own way. */
export interface PublicRouteFetch {
  ozPlan(key: string): Promise<PublicRouteHttp>;
  avplanLive(): Promise<PublicRouteHttp>;
  avplanPlan(device: string): Promise<PublicRouteHttp>;
}

/** A route asked: the route, none (`route` and `problem` null: the pilot shared none), or why it could not be read. */
export interface PublicRouteResult {
  readonly route: PublicRoute | null;
  readonly problem: string | null;
}

export interface PublicRouteRow {
  readonly label: string;
  readonly value: string;
}

/** What the map draws: the points (with their hover words), solid or dashed, and the line's own label. */
export interface PublicRouteLine {
  readonly points: readonly { readonly lat: number; readonly lon: number; readonly tip: string | null }[];
  readonly schedule: boolean;
  readonly label: string | null;
}

export interface PublicRouteView {
  readonly rows: readonly PublicRouteRow[];
  readonly caption: string | null;
  readonly line: PublicRouteLine | null;
}

export interface PublicRouteParsed {
  readonly value: PublicRoute | null;
  /** Why nothing was read, in words; null when the answer was read (with or without a route). */
  readonly problem: string | null;
}

// ---------------------------------------------------------------------------------------------- constants

/** The fixed addresses (Teslyn Field's Rust core holds its own copy and checks the keys the same way). */
export const OZ_PLAN_URL = 'https://tx.ozrunways.com/tx/plan?key=';
export const AVPLAN_LIVE_URL = 'https://avplan-efb.firebaseio.com/live.json';
export const AVPLAN_PLAN_URL = 'https://avplan-efb.firebaseio.com/live-plan/';

/** A route the aircraft is further than this from is another flight's: not shown. */
export const ROUTE_MAX_OFF_NM = 100;
/** An AvPlan plan whose last time is older than this is an old plan: not shown. */
export const ROUTE_MAX_AGE_MS = 12 * 3_600_000;
/** An Oz Runways route ending this close to a listed aerodrome is named after it. */
export const ROUTE_DESTINATION_NM = 1;
/** The airline line's own words (decision 934). */
export const SCHEDULE_LABEL = 'Published schedule, not this flight’s plan';
/** How Teslyn Cloud's function keeps AvPlan's `live.json` between clicks. */
export const AVPLAN_LIVE_KEEP_MS = 30_000;
/** Routes one person may ask Teslyn Cloud for in a minute. */
export const ROUTES_PER_MINUTE = 20;

const NAME: Readonly<Record<PublicRouteSource, string>> = {
  ozrunways: 'Oz Runways',
  avplan: 'AvPlan',
  airline: 'adsbdb',
};

/** Oz Runways' track key as `tx/geo` sends it (five letters and digits on 2 Nov 2026). */
export const ozPlanKeyOk = (k: string): boolean => /^[A-Za-z0-9]{1,32}$/.test(k);
/** An AvPlan device id as `live.json` keys them (letters, digits and dashes, 25 to 32 long on 2 Nov 2026). */
export const avplanKeyOk = (k: string): boolean => /^[A-Za-z0-9-]{8,64}$/.test(k);

// ---------------------------------------------------------------------------------------------- geometry

const EARTH_KM = 6371.0088;
const KM_PER_NM = 1.852;
const RAD = Math.PI / 180;

/** Angular distance in radians. */
function angle(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const h =
    Math.sin(((b.lat - a.lat) * RAD) / 2) ** 2 +
    Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(((b.lon - a.lon) * RAD) / 2) ** 2;
  return 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

const toNm = (rad: number): number => (rad * EARTH_KM) / KM_PER_NM;

/** Nautical miles between two points. */
export const routeNm = (a: { lat: number; lon: number }, b: { lat: number; lon: number }): number =>
  toNm(angle(a, b));

function bearing(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const y = Math.sin((b.lon - a.lon) * RAD) * Math.cos(b.lat * RAD);
  const x =
    Math.cos(a.lat * RAD) * Math.sin(b.lat * RAD) -
    Math.sin(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.cos((b.lon - a.lon) * RAD);
  return Math.atan2(y, x);
}

/** Nautical miles from a point to the leg a→b on the great circle: across the leg, else to its nearer end. */
function legNm(
  p: { lat: number; lon: number },
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
) {
  const ab = angle(a, b);
  const ap = angle(a, p);
  if (ab < 1e-9 || ap < 1e-12) return toNm(ap);
  const turn = bearing(a, p) - bearing(a, b);
  const across = Math.asin(Math.max(-1, Math.min(1, Math.sin(ap) * Math.sin(turn))));
  const along = Math.acos(Math.max(-1, Math.min(1, Math.cos(ap) / Math.cos(across))));
  if (Math.cos(turn) < 0 || along > ab) return toNm(Math.min(ap, angle(b, p)));
  return toNm(Math.abs(across));
}

/** The aircraft's distance from a route: the nearest of its legs (a route of one point: that point). */
export function routeOffNm(
  points: readonly { lat: number; lon: number }[],
  at: { lat: number; lon: number },
): number {
  if (points.length === 0) return Number.POSITIVE_INFINITY;
  if (points.length === 1) return routeNm(points[0]!, at);
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < points.length; i += 1) best = Math.min(best, legNm(at, points[i - 1]!, points[i]!));
  return best;
}

/** The great circle from a to b in legs of about 100 nm (at most 64), ends included. */
function greatCircle(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): { lat: number; lon: number }[] {
  const d = angle(a, b);
  const n = Math.min(64, Math.max(1, Math.ceil(toNm(d) / 100)));
  if (d < 1e-9) return [a, b];
  const out: { lat: number; lon: number }[] = [];
  for (let i = 0; i <= n; i += 1) {
    const f = i / n;
    const s1 = Math.sin((1 - f) * d) / Math.sin(d);
    const s2 = Math.sin(f * d) / Math.sin(d);
    const x =
      s1 * Math.cos(a.lat * RAD) * Math.cos(a.lon * RAD) + s2 * Math.cos(b.lat * RAD) * Math.cos(b.lon * RAD);
    const y =
      s1 * Math.cos(a.lat * RAD) * Math.sin(a.lon * RAD) + s2 * Math.cos(b.lat * RAD) * Math.sin(b.lon * RAD);
    const z = s1 * Math.sin(a.lat * RAD) + s2 * Math.sin(b.lat * RAD);
    out.push({ lat: Math.atan2(z, Math.hypot(x, y)) / RAD, lon: Math.atan2(y, x) / RAD });
  }
  out[0] = { lat: a.lat, lon: a.lon };
  out[n] = { lat: b.lat, lon: b.lon };
  return out;
}

/** The listed aerodrome within `nm` of a point, the nearest; null when none. */
export function aerodromeWithinNm(p: { lat: number; lon: number }, nm: number): PublicRoutePlace | null {
  const span = nm / 60 + 0.02;
  let best: { code: string; name: string; d: number } | null = null;
  for (const a of auAerodromeList()) {
    if (Math.abs(a.lat - p.lat) > span || Math.abs(a.lon - p.lon) > span * 3) continue;
    const d = routeNm(a, p);
    if (d <= nm && (!best || d < best.d)) best = { code: a.code, name: a.name, d };
  }
  return best ? { code: best.code, name: best.name } : null;
}

// ---------------------------------------------------------------------------------------------- readers

const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const onGlobe = (lat: number, lon: number): boolean =>
  lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 && !(lat === 0 && lon === 0);
const secondsMs = (v: unknown): number | null => {
  const s = num(v);
  return s !== null && s > 0 ? Math.round(s * 1000) : null;
};

/** A callsign as AvPlan's reader keys aircraft: spaces and dashes removed, upper case. */
const callsignKey = (c: string): string => c.replace(/[\s-]+/g, '').toUpperCase();

/**
 * Which route a click on this aircraft asks for, from its id on the map (`ozrunways::<tk>`, `avplan::<callsign>`, an
 * ADS-B aircraft with an airline flight callsign); null for the organisation's own aircraft and anything else.
 */
export function publicRouteAsk(a: {
  readonly id: string;
  readonly label: string;
  readonly callsign?: string | null;
  readonly fleet?: boolean;
}): PublicRouteAsk | null {
  if (a.fleet === true) return null;
  const m = /^(ozrunways|avplan|adsb|opensky):[^:]*:(.+)$/.exec(a.id);
  if (!m) return null;
  const source = m[1]!;
  const device = m[2]!;
  if (source === 'ozrunways') return ozPlanKeyOk(device) ? { source, key: device } : null;
  if (source === 'avplan') {
    // the aircraft's id is its callsign, or its own device id when it has none (public-traffic.ts parseAvPlan)
    const label = str(a.label);
    const hasCallsign = label !== '' && label !== 'AvPlan' && callsignKey(label) === callsignKey(device);
    return { source, callsign: hasCallsign ? callsignKey(device) : '', deviceId: device };
  }
  const cs = airlineCallsign(a.callsign ?? a.label);
  return cs ? { source: 'airline', callsign: cs } : null;
}

/**
 * The AvPlan device whose plan to ask, from a `live.json` answer: the entries with the aircraft's callsign (or, for
 * an aircraft without one, its own entry), never one AvPlan marks `avplanTraffic: false` (ADS-B it relays, never a
 * plan); of two with one callsign, the nearest to the clicked aircraft, else the newest. Null when none.
 */
export function avplanDevice(
  liveBody: string,
  ask: { readonly callsign: string; readonly deviceId: string },
  near: { readonly lat: number; readonly lon: number } | null,
): string | null {
  let data: unknown;
  try {
    data = JSON.parse(liveBody);
  } catch {
    return null;
  }
  const want = callsignKey(ask.callsign);
  let best: { key: string; d: number; t: number } | null = null;
  for (const [key, raw] of Object.entries(obj(data))) {
    const e = obj(raw);
    if (e.avplanTraffic === false || !avplanKeyOk(key)) continue;
    const mine = want
      ? callsignKey(str(e.callsign)) === want
      : key === ask.deviceId || str(e.udid) === ask.deviceId;
    if (!mine) continue;
    const lat = num(e.latitude);
    const lon = num(e.longitude);
    const d = near && lat !== null && lon !== null ? routeNm(near, { lat, lon }) : 0;
    const t = num(e.timestamp) ?? 0;
    if (!best || d < best.d - 1e-6 || (Math.abs(d - best.d) <= 1e-6 && t > best.t)) best = { key, d, t };
  }
  return best?.key ?? null;
}

/**
 * Oz Runways' `tx/plan` answer: one LineString of [lon, lat] (sent as text), `properties.name` the track key asked;
 * empty when the pilot shared no plan. The destination is the listed aerodrome within 1 nm of the last point.
 */
export function parseOzRunwaysPlan(raw: string, key: string): PublicRouteParsed {
  let data: Record<string, unknown>;
  try {
    data = obj(JSON.parse(raw));
  } catch {
    return { value: null, problem: 'The Oz Runways plan is not JSON.' };
  }
  if (!Array.isArray(data.features)) return { value: null, problem: 'The Oz Runways plan has no route.' };
  const f = obj(data.features[0]);
  const name = str(obj(f.properties).name);
  if (name && name !== key) return { value: null, problem: 'Oz Runways sent the plan of another aircraft.' };
  const coords = obj(f.geometry).coordinates;
  const points: PublicRoutePoint[] = [];
  for (const c of Array.isArray(coords) ? coords : []) {
    if (!Array.isArray(c)) continue;
    const lon = num(c[0]);
    const lat = num(c[1]);
    if (lat === null || lon === null || !onGlobe(lat, lon)) continue;
    points.push({ lat, lon, ident: null, altFt: null, etaMs: null, passedMs: null, landing: false });
  }
  if (points.length < 2) return { value: null, problem: null };
  return {
    value: {
      source: 'ozrunways',
      points,
      schedule: false,
      origin: aerodromeWithinNm(points[0]!, ROUTE_DESTINATION_NM),
      destination: aerodromeWithinNm(points[points.length - 1]!, ROUTE_DESTINATION_NM),
    },
    problem: null,
  };
}

/** A waypoint's place: its aerodrome's name when its ident is one, else the aerodrome within 1 nm, else its ident. */
function placeOf(p: PublicRoutePoint): PublicRoutePlace | null {
  const listed = p.ident ? AU_AERODROMES.get(p.ident) : undefined;
  if (listed) return { code: listed.code, name: listed.name };
  const near = aerodromeWithinNm(p, ROUTE_DESTINATION_NM);
  if (near) return near;
  return p.ident ? { code: p.ident, name: null } : null;
}

/**
 * AvPlan's `live-plan/<device>.json`: `activePlan.points` (the flight being flown) in sequence order, each with its
 * ident, position, altitude, ETA (`eta`, else `planedEta`, seconds) and time passed (`ata`). The plan's callsign,
 * type and id are not read. `null` or a plan without two positioned points: no route.
 */
export function parseAvPlanPlan(raw: string): PublicRouteParsed {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { value: null, problem: 'The AvPlan plan is not JSON.' };
  }
  const plan = obj(obj(data).activePlan);
  const listed = plan.points;
  const entries: [string, unknown][] = Array.isArray(listed)
    ? listed.map((v, i) => [String(i).padStart(3, '0'), v])
    : Object.entries(obj(listed));
  const rows = entries
    .map(([k, v]) => ({ k, v: obj(v) }))
    .map(({ k, v }) => ({ k, v, seq: num(v.sequence) }))
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0) || (a.k < b.k ? -1 : a.k > b.k ? 1 : 0));
  const points: PublicRoutePoint[] = [];
  for (const { v } of rows) {
    const lat = num(v.latitude);
    const lon = num(v.longitude);
    if (lat === null || lon === null || !onGlobe(lat, lon)) continue;
    points.push({
      lat,
      lon,
      ident: str(v.ident).slice(0, 24) || null,
      altFt: num(v.altitude),
      etaMs: secondsMs(v.eta) ?? secondsMs(v.planedEta),
      passedMs: secondsMs(v.ata),
      landing: v.landingPoint === true,
    });
  }
  if (points.length < 2) return { value: null, problem: null };
  return {
    value: {
      source: 'avplan',
      points,
      schedule: false,
      origin: placeOf(points[0]!),
      destination: placeOf(points[points.length - 1]!),
    },
    problem: null,
  };
}

/** An airport of adsbdb's answer (`aircraft-register.ts` RouteAirport) as this module reads it. */
export interface ScheduleAirport {
  readonly icao: string | null;
  readonly iata: string | null;
  readonly name: string | null;
  readonly municipality: string | null;
  readonly lat?: number | null;
  readonly lon?: number | null;
}

/**
 * An airline flight's published schedule as a line (decision 934): the great circle between its two airports, in legs
 * of about 100 nm so the map and the 100 nm rule follow the same line. Null without both airports' positions.
 */
export function scheduleRoute(r: {
  readonly found: boolean;
  readonly origin: ScheduleAirport | null;
  readonly destination: ScheduleAirport | null;
}): PublicRoute | null {
  const o = r.origin;
  const d = r.destination;
  if (!r.found || !o || !d) return null;
  const at = (a: ScheduleAirport) =>
    typeof a.lat === 'number' && typeof a.lon === 'number' && onGlobe(a.lat, a.lon)
      ? { lat: a.lat, lon: a.lon }
      : null;
  const from = at(o);
  const to = at(d);
  if (!from || !to) return null;
  const place = (a: ScheduleAirport): PublicRoutePlace | null => {
    const code = a.icao ?? a.iata;
    return code ? { code, name: a.municipality ?? a.name } : null;
  };
  const line = greatCircle(from, to);
  return {
    source: 'airline',
    points: line.map((p, i) => ({
      lat: p.lat,
      lon: p.lon,
      ident: i === 0 ? (o.icao ?? o.iata) : i === line.length - 1 ? (d.icao ?? d.iata) : null,
      altFt: null,
      etaMs: null,
      passedMs: null,
      landing: false,
    })),
    schedule: true,
    origin: place(o),
    destination: place(d),
  };
}

// ---------------------------------------------------------------------------------------------- asking

function feedProblem(name: string, res: PublicRouteHttp): string | null {
  if (res.status === 200) return null;
  if (res.status === 429 || res.status === 403)
    return `${name} asked Teslyn to wait. Try again in a few minutes.`;
  return `${name} answered HTTP ${res.status}.`;
}

/**
 * Ask a pilot's route for one click: Oz Runways' `tx/plan` by track key, or AvPlan's `live.json` for the device and
 * then its `live-plan`. The device id is used for that one request only. `near` is the clicked aircraft's position
 * (two AvPlan entries can share a callsign).
 */
export async function fetchPublicRoute(
  ask: Extract<PublicRouteAsk, { source: 'ozrunways' | 'avplan' }>,
  near: { readonly lat: number; readonly lon: number } | null,
  f: PublicRouteFetch,
): Promise<PublicRouteResult> {
  const name = NAME[ask.source];
  try {
    if (ask.source === 'ozrunways') {
      if (!ozPlanKeyOk(ask.key)) return { route: null, problem: 'Oz Runways has no plan for this aircraft.' };
      const res = await f.ozPlan(ask.key);
      if (res.status === 404) return { route: null, problem: null };
      const bad = feedProblem(name, res);
      if (bad) return { route: null, problem: bad };
      const p = parseOzRunwaysPlan(res.body, ask.key);
      return { route: p.value, problem: p.problem };
    }
    const live = await f.avplanLive();
    const bad = feedProblem(name, live);
    if (bad) return { route: null, problem: bad };
    const device = avplanDevice(live.body, ask, near);
    if (!device) return { route: null, problem: null };
    const res = await f.avplanPlan(device);
    if (res.status === 404) return { route: null, problem: null };
    const bad2 = feedProblem(name, res);
    if (bad2) return { route: null, problem: bad2 };
    const p = parseAvPlanPlan(res.body);
    return { route: p.value, problem: p.problem };
  } catch (e) {
    return {
      route: null,
      problem: e instanceof Error && e.message ? e.message : `${name} cannot be reached.`,
    };
  }
}

// ---------------------------------------------------------------------------------------------- the rule

/** The last time an AvPlan plan gives (any point's ETA or time passed); null when it gives none. */
export function routeLastMs(route: PublicRoute): number | null {
  let last: number | null = null;
  for (const p of route.points)
    for (const t of [p.etaMs, p.passedMs]) if (t !== null && (last === null || t > last)) last = t;
  return last;
}

const grouped = (n: number): string => Math.round(n).toLocaleString('en-AU');

function ageWords(ms: number): string {
  const h = ms / 3_600_000;
  if (h < 48) return `${Math.max(1, Math.round(h))} hours ago`;
  return `${Math.round(h / 24)} days ago`;
}

/**
 * Whether the route is shown with the aircraft where it is now: not when the aircraft is over 100 nm from it (another
 * flight's plan or schedule), nor when an AvPlan plan's last time is over 12 hours old. `words` says why not.
 */
export function publicRouteShown(
  route: PublicRoute,
  at: { readonly lat: number; readonly lon: number },
  nowMs: number,
): { readonly shown: boolean; readonly words: string | null } {
  if (route.source === 'avplan') {
    const last = routeLastMs(route);
    if (last !== null && nowMs - last > ROUTE_MAX_AGE_MS)
      return { shown: false, words: `Not shown: its last time was ${ageWords(nowMs - last)}.` };
  }
  const off = routeOffNm(route.points, at);
  if (off > ROUTE_MAX_OFF_NM)
    return { shown: false, words: `Not shown: the aircraft is ${grouped(off)} nm from it.` };
  return { shown: true, words: null };
}

// ---------------------------------------------------------------------------------------------- words

const hhmm = (utcMs: number, offsetH: number): string => {
  const d = new Date(utcMs + Math.round(offsetH * 3_600_000));
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

const placeWords = (p: PublicRoutePlace): string => (p.name ? `${p.code} ${p.name}` : p.code);

/** The idents in order: all of them up to 8, else the first three, "…" and the last three, with the count. */
function identLine(route: PublicRoute): string {
  const ids = route.points.map((p) => p.ident ?? '?');
  const shown = ids.length <= 8 ? ids : [...ids.slice(0, 3), '…', ...ids.slice(-3)];
  return `${shown.join(' → ')} (${ids.length} points)`;
}

/** The first point after the last one passed (AvPlan); null when every point is passed or none is. */
function nextPoint(route: PublicRoute): PublicRoutePoint | null {
  let lastPassed = -1;
  route.points.forEach((p, i) => {
    if (p.passedMs !== null) lastPassed = i;
  });
  if (lastPassed < 0) return null;
  return route.points[lastPassed + 1] ?? null;
}

const pointTip = (p: PublicRoutePoint, offsetH: number): string | null => {
  if (!p.ident) return null;
  const parts = [p.ident];
  if (p.altFt !== null && p.altFt > 0) parts.push(`${grouped(p.altFt)} ft`);
  if (p.passedMs !== null) parts.push(`passed ${hhmm(p.passedMs, offsetH)}`);
  else if (p.etaMs !== null) parts.push(`ETA ${hhmm(p.etaMs, offsetH)}`);
  return parts.join(' · ');
};

/** The caption under the rows, naming where the route came from. */
export function publicRouteCaption(source: PublicRouteSource): string {
  if (source === 'ozrunways')
    return 'Route: Oz Runways (the pilot’s planned route, asked when this card opened; not kept)';
  if (source === 'avplan') return 'Route: AvPlan (the pilot’s plan, asked when this card opened; not kept)';
  return 'Route: adsbdb (the airline’s published schedule, not this flight’s plan)';
}

/**
 * What the card and the map show for a route asked: the rows (the route, the next point and the destination with
 * their ETAs for AvPlan; the points and the destination for Oz Runways; why it is not shown), the caption and the
 * line. `asking` while the answer is on its way. An airline's rows stay `routeCardRows`; here only its line, or why
 * it is not drawn. Times in the project's time zone (`offsetH`).
 */
export function publicRouteView(
  state: PublicRouteResult | 'asking',
  source: PublicRouteSource,
  at: { readonly lat: number; readonly lon: number },
  nowMs: number,
  offsetH: number,
): PublicRouteView {
  const label = source === 'airline' ? 'Schedule line' : 'Planned route';
  if (state === 'asking')
    return source === 'airline'
      ? { rows: [], caption: null, line: null }
      : { rows: [{ label, value: `Asking ${NAME[source]}…` }], caption: null, line: null };
  if (state.problem) return { rows: [{ label, value: state.problem }], caption: null, line: null };
  const route = state.route;
  if (!route)
    return source === 'airline'
      ? { rows: [], caption: null, line: null }
      : {
          rows: [{ label, value: 'None shared by the pilot.' }],
          caption: publicRouteCaption(source),
          line: null,
        };
  const shown = publicRouteShown(route, at, nowMs);
  if (!shown.shown)
    return {
      rows: [{ label, value: shown.words! }],
      caption: source === 'airline' ? null : publicRouteCaption(source),
      line: null,
    };
  const rows: PublicRouteRow[] = [];
  if (route.source === 'avplan') {
    rows.push({ label, value: identLine(route) });
    const next = nextPoint(route);
    if (next?.ident)
      rows.push({
        label: 'Next point',
        value: next.etaMs !== null ? `${next.ident}, ETA ${hhmm(next.etaMs, offsetH)}` : next.ident,
      });
    const end = route.points[route.points.length - 1]!;
    if (route.destination)
      rows.push({
        label: 'Destination',
        value:
          end.passedMs === null && end.etaMs !== null
            ? `${placeWords(route.destination)}, ETA ${hhmm(end.etaMs, offsetH)}`
            : placeWords(route.destination),
      });
  } else if (route.source === 'ozrunways') {
    rows.push({ label, value: `${route.points.length} points, shown on the map` });
    if (route.destination) rows.push({ label: 'Destination', value: placeWords(route.destination) });
  }
  return {
    rows,
    caption: route.source === 'airline' ? null : publicRouteCaption(route.source),
    line: {
      points: route.points.map((p) => ({
        lat: p.lat,
        lon: p.lon,
        tip: route.source === 'avplan' ? pointTip(p, offsetH) : null,
      })),
      schedule: route.schedule,
      label: route.schedule ? SCHEDULE_LABEL : null,
    },
  };
}

// ---------------------------------------------------------------------------------------------- the cloud request

export interface TrafficRouteRequest {
  readonly tenantId: string;
  readonly source: 'ozrunways' | 'avplan';
  /** The aircraft's id in the organisation's public traffic (`cloud_traffic_position.source_id`). */
  readonly id: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The `traffic-route` function's body: `{ tenant_id, source: 'ozrunways' | 'avplan', id }`. */
export function parseTrafficRouteRequest(
  body: unknown,
):
  | { readonly ok: true; readonly value: TrafficRouteRequest }
  | { readonly ok: false; readonly error: string } {
  const b = obj(body);
  const tenant = str(b.tenant_id);
  if (!UUID.test(tenant)) return { ok: false, error: 'tenant_id must be an organisation id.' };
  if (b.source !== 'ozrunways' && b.source !== 'avplan')
    return { ok: false, error: 'Ask for an Oz Runways or AvPlan route.' };
  const id = str(b.id);
  if (!id || id.length > 32 || !/^[A-Za-z0-9 _.-]+$/.test(id))
    return { ok: false, error: 'id must be the aircraft’s id in the public traffic.' };
  return { ok: true, value: { tenantId: tenant.toLowerCase(), source: b.source, id } };
}

/** The ask for an aircraft of the cloud's public traffic (`source_id`, with its label for AvPlan's callsign). */
export function cloudRouteAsk(
  req: TrafficRouteRequest,
  label: string,
): Extract<PublicRouteAsk, { source: 'ozrunways' | 'avplan' }> | null {
  return publicRouteAsk({ id: `${req.source}::${req.id}`, label }) as Extract<
    PublicRouteAsk,
    { source: 'ozrunways' | 'avplan' }
  > | null;
}
